import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";

import { useDrag, useDrop } from "react-dnd";
import { Bars3Icon } from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { ITruck } from "../../models/Truck";

const DRAG_TYPE = "TRUCK_ORDER_ITEM";

interface DraggableTruckProps {
  truck: ITruck;
  index: number;
  moveItem: (from: number, to: number) => void;
}

function DraggableTruck({ truck, index, moveItem }: DraggableTruckProps) {
  const ref = useRef<HTMLLIElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPE,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: DRAG_TYPE,
    hover(item: { index: number }) {
      if (item.index === index) return;
      moveItem(item.index, index);
      item.index = index;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  drag(drop(ref));

  return (
    <li
      ref={ref}
      className={`flex items-center gap-3 p-3 border rounded cursor-grab active:cursor-grabbing select-none transition-colors ${
        isDragging ? "opacity-40 bg-blue-50 border-blue-200" : isOver ? "bg-blue-200 border-blue-400" : "bg-blue-50 border-blue-200"
      }`}
    >
      <Bars3Icon className="h-5 w-5 text-gray-400 flex-shrink-0 pointer-events-none" />
      <span className="text-sm font-medium text-gray-900 pointer-events-none">{truck.name}</span>
    </li>
  );
}

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  trucks: ITruck[];
  onConfirm: (orderedTrucks: ITruck[]) => void;
}

export default function TruckOrderModal({ open, setOpen, trucks, onConfirm }: Props) {
  const [orderedTrucks, setOrderedTrucks] = useState<ITruck[]>([...trucks]);

  useEffect(() => {
    if (open) setOrderedTrucks([...trucks]);
  }, [open]);

  const moveItem = useCallback((from: number, to: number) => {
    setOrderedTrucks((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }, []);

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-md">
      <h3 className="text-lg font-medium text-gray-900 mb-1">Print Order</h3>
      <p className="text-sm text-gray-500 mb-4">Drag trucks into the order you want them printed.</p>
      <ul className="space-y-2 mb-6">
        {orderedTrucks.map((truck, i) => (
          <DraggableTruck key={truck.id} truck={truck} index={i} moveItem={moveItem} />
        ))}
      </ul>
      <div className="flex justify-between">
        <ButtonInput
          label="Print"
          isSubmit={false}
          onClick={() => {
            onConfirm(orderedTrucks);
            setOpen(false);
          }}
          classes={undefined}
        />
        <ButtonInput
          label="Cancel"
          isSubmit={false}
          onClick={() => setOpen(false)}
          classes="border border-gray-300 !bg-white !text-gray-700 hover:!bg-gray-50"
        />
      </div>
    </Modal>
  );
}
